import { createRouter, createWebHistory } from 'vue-router'
import { useAuth } from '@/hooks/useAuth'
import Index from '../pages/Index.vue'
import Login from '../pages/auth/Login.vue'
import Register from '../pages/auth/Register.vue'
import ForgotPassword from '../pages/auth/ForgotPassword.vue'
import DashboardLayout from '../layouts/DashboardLayout.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: Index,
      meta: { guest: true }
    },
    // Dashboard routes - todas las rutas autenticadas usan DashboardLayout como padre
    {
      path: '/',
      component: DashboardLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: 'trends',
          name: 'trends',
          component: () => import('../pages/Trends.vue'),
          meta: { requiredPermission: 'trends.view' }
        },
        {
          path: 'realtime',
          name: 'realtime',
          component: () => import('../pages/Realtime.vue'),
          meta: { requiredPermission: 'realtime.view' }
        },
        {
          path: 'app/employee/:state',
          name: 'employees',
          component: () => import('../pages/Employees.vue'),
          meta: { requiredPermission: 'employees.view' }
        },
        {
          path: 'app/teams',
          name: 'teams',
          component: () => import('../pages/Teams.vue'),
          meta: { requiredPermission: 'teams.view' }
        },
        {
          path: 'app/teams/:id/detail/:tab',
          name: 'team-detail',
          component: () => import('../pages/TeamDetail.vue'),
          meta: { requiredPermission: 'teams.view' }
        },
        {
          path: 'app/teams/:id/detail',
          redirect: to => ({ name: 'team-detail', params: { id: to.params.id, tab: 'overview' } })
        },
        {
          path: 'app/employee/:id/detail/:tab',
          name: 'employee-detail',
          component: () => import('../pages/EmployeeDetail.vue'),
          meta: { requiresAuth: true, requiredPermission: 'employees.view' }
        },
        {
          path: 'app/employee/:id/detail',
          redirect: to => ({ name: 'employee-detail', params: { id: to.params.id, tab: 'utilization' } })
        },
        {
          path: 'activities/:view',
          name: 'activities',
          component: () => import('../pages/Activities.vue'),
          meta: { requiredPermission: 'activities.view' }
        },
        {
          path: 'alerts',
          name: 'alerts',
          component: () => import('../pages/Alerts.vue'),
          meta: { requiredPermission: 'alerts.view' }
        },
        {
          path: 'screens',
          name: 'screens',
          component: () => import('../pages/Screens.vue'),
          meta: { requiredPermission: 'screens.view' }
        },
        {
          path: 'reports',
          name: 'reports',
          component: () => import('../pages/Reports.vue'),
          meta: { requiredPermission: 'reports.view' }
        },
        {
          path: 'reports/apps-websites',
          name: 'reports-apps-websites',
          component: () => import('../pages/ReportsAppsWebsites.vue'),
          meta: { requiredPermission: 'reports.view' }
        },
        {
          path: 'reports/location-insights',
          name: 'reports-location-insights',
          component: () => import('../pages/ReportsLocationInsights.vue'),
          meta: { requiredPermission: 'reports.view' }
        },
        {
          path: 'reports/work-types',
          name: 'reports-work-types',
          component: () => import('../pages/ReportsWorkTypes.vue'),
          meta: { requiredPermission: 'reports.view' }
        },
        {
          path: 'reports/work-types/:slug',
          name: 'reports-work-type-detail',
          component: () => import('../pages/ReportsWorkTypeDetail.vue'),
          meta: { requiredPermission: 'reports.view' }
        },
        {
          path: 'app/settings',
          name: 'settings',
          component: () => import('../pages/Settings.vue'),
          meta: { requiresAuth: true, requiresAdminOrManager: true }
        },
        {
          path: 'app/settings/tracking',
          name: 'settings-tracking',
          component: () => import('../pages/SettingsTracking.vue'),
          meta: { requiresAuth: true, requiresAdminOrManager: true }
        },
        {
          path: 'app/settings/privacy',
          name: 'settings-privacy',
          component: () => import('../pages/SettingsPrivacy.vue'),
          meta: { requiresAuth: true, requiresAdminOrManager: true }
        },
        {
          path: 'app/settings/employee_portal',
          name: 'settings-employee-portal',
          component: () => import('../pages/SettingsEmployeePortal.vue'),
          meta: { requiresAuth: true, requiresAdmin: true }
        },
        {
          path: 'app/settings/user_admin',
          name: 'settings-users',
          component: () => import('../pages/SettingsUsers.vue'),
          meta: { requiresAuth: true, requiresAdminOrManager: true }
        },
        {
          path: 'app/settings/profile',
          name: 'settings-profile',
          component: () => import('../pages/SettingsProfile.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'app/settings/organization',
          name: 'settings-organization',
          component: () => import('../pages/SettingsOrganization.vue'),
          meta: { requiresAuth: true, requiresAdminOrManager: true }
        },
        {
          path: 'app/settings/productivity',
          name: 'settings-productivity',
          component: () => import('../pages/SettingsProductivity.vue'),
          meta: { requiresAuth: true, requiresAdminOrManager: true }
        },
        {
          path: 'app/settings/locations',
          name: 'settings-locations',
          component: () => import('../pages/SettingsLocations.vue'),
          meta: { requiresAuth: true, requiresAdminOrManager: true }
        },
        {
          path: 'app/settings/schedules',
          name: 'settings-schedules',
          component: () => import('../pages/SettingsSchedules.vue'),
          meta: { requiresAuth: true, requiredPermission: 'schedules.view' }
        },
        {
          path: 'app/settings/utilization',
          name: 'settings-utilization',
          component: () => import('../pages/SettingsUtilization.vue'),
          meta: { requiresAuth: true, requiresAdminOrManager: true }
        },
        {
          path: 'app/settings/billing',
          name: 'settings-billing',
          component: () => import('../pages/SettingsBilling.vue'),
          meta: { requiresAuth: true, requiresAdmin: true }
        },
        {
          path: 'app/settings/audit_logs',
          name: 'settings-audit-logs',
          component: () => import('../pages/SettingsAuditLogs.vue'),
          meta: { requiresAuth: true, requiresAdmin: true }
        },
        {
          path: 'app/settings/productivity/app/:id',
          name: 'settings-productivity-app',
          component: () => import('../components/productivity/app-detail/SettingsProductivityApp.vue'),
          meta: { requiresAuth: true, requiresAdminOrManager: true },
          children: [
            {
              path: 'overview',
              name: 'settings-productivity-app-overview',
              component: () => import('../components/productivity/app-detail/AppDetailOverview.vue')
            },
            {
              path: 'teams',
              name: 'settings-productivity-app-teams',
              component: () => import('../components/productivity/app-detail/AppDetailTeams.vue')
            },
            {
              path: '',
              redirect: { name: 'settings-productivity-app-overview' }
            }
          ]
        }
      ]
    },
    // Redirects
    {
      path: '/activities',
      redirect: '/activities/timeline'
    },
    {
      path: '/employees',
      redirect: '/app/employee/active'
    },
    {
      path: '/teams',
      redirect: '/app/teams'
    },
    {
      path: '/dashboard',
      redirect: '/trends'
    },
    // Test route (pública para debugging de componentes)
    {
      path: '/test',
      name: 'test-components',
      component: () => import('../pages/TestComponents.vue'),
      meta: { guest: true }
    },
    // Auth routes (sin layout)
    {
      path: '/login',
      name: 'login',
      component: Login,
      meta: { guest: true }
    },
    {
      path: '/register',
      name: 'register',
      component: Register,
      meta: { guest: true }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPassword,
      meta: { guest: true }
    },
    {
      path: '/accept-invitation',
      name: 'accept-invitation',
      component: () => import('../pages/auth/AcceptInvitation.vue'),
      meta: { guest: true }
    },
    {
      path: '/install',
      name: 'install',
      component: () => import('../pages/InstallPage.vue'),
      meta: { guest: true, skipAuthRedirect: true }
    },
    {
      path: '/2fa-verify',
      name: '2fa-verify',
      component: () => import('../pages/auth/TwoFactorVerify.vue'),
      meta: { guest: true }
    },
    {
      path: '/2fa-setup',
      name: '2fa-setup',
      component: () => import('../pages/auth/TwoFactorSetup.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/onboarding',
      name: 'onboarding',
      component: () => import('../pages/auth/Onboarding.vue'),
      meta: { requiresAuth: true }
    },
    // Backoffice routes (separate layout, superadmin only)
    {
      path: '/backoffice',
      component: () => import('../layouts/BackofficeLayout.vue'),
      meta: { requiresAuth: true, requiresSuperAdmin: true },
      children: [
        {
          path: '',
          name: 'backoffice-dashboard',
          component: () => import('../pages/backoffice/BackofficeDashboard.vue')
        },
        {
          path: 'organizations/:id',
          name: 'backoffice-org-detail',
          component: () => import('../pages/backoffice/BackofficeOrgDetail.vue')
        },
        {
          path: 'organizations/:id/offers/create',
          name: 'backoffice-offer-create',
          component: () => import('../pages/backoffice/BackofficeOfferForm.vue')
        },
        {
          path: 'offer-requests',
          name: 'backoffice-offer-requests',
          component: () => import('../pages/backoffice/BackofficeOfferRequests.vue')
        },
        {
          path: 'plans',
          name: 'backoffice-plans',
          component: () => import('../pages/backoffice/BackofficePlans.vue')
        },
        {
          path: 'plans/create',
          name: 'backoffice-plan-create',
          component: () => import('../pages/backoffice/BackofficePlanForm.vue')
        },
        {
          path: 'plans/:id/edit',
          name: 'backoffice-plan-edit',
          component: () => import('../pages/backoffice/BackofficePlanForm.vue')
        },
        {
          path: 'releases',
          name: 'backoffice-releases',
          component: () => import('../pages/backoffice/BackofficeReleases.vue')
        },
        {
          path: 'app-management',
          name: 'backoffice-app-management',
          component: () => import('../pages/backoffice/BackofficeAppManagement.vue'),
          children: [
            { path: '', name: 'backoffice-canonical-apps', component: () => import('../pages/backoffice/BackofficeCanonicalApps.vue') },
            { path: 'unlinked', name: 'backoffice-unlinked-apps', component: () => import('../pages/backoffice/BackofficeUnlinkedApps.vue') },
            { path: 'proposals', name: 'backoffice-app-proposals', component: () => import('../pages/backoffice/BackofficeProposals.vue') },
            { path: ':id', name: 'backoffice-canonical-app-detail', component: () => import('../pages/backoffice/BackofficeCanonicalAppDetail.vue') },
          ]
        },
        {
          path: 'settings',
          name: 'backoffice-settings',
          component: () => import('../pages/backoffice/BackofficeSettings.vue')
        }
      ]
    }
  ]
})

router.beforeEach(async (to, from, next) => {
  const { user, fetchUser, isLoading, is2FASetupRequired } = useAuth()

  // Helper: check if 2FA setup is required (from user object OR sessionStorage)
  const needs2FASetup = () => {
    return user.value?.setup_2fa_required || is2FASetupRequired()
  }

  // For guest routes, check if user is authenticated first
  if (to.meta.guest) {
    // If no user loaded and not loading, try to fetch
    if (!user.value && !isLoading.value) {
      try {
        await fetchUser()
      } catch (e) {
        // No session - but check if there's a pending 2FA in storage
        if (is2FASetupRequired()) {
          return next('/2fa-setup')
        }
        // No session, allow access to guest route
        return next()
      }
    }

    // If still no user, allow access (but check storage)
    if (!user.value) {
      if (is2FASetupRequired()) {
        return next('/2fa-setup')
      }
      return next()
    }

    // User is authenticated, check if 2FA setup is required
    if (needs2FASetup()) {
      return next('/2fa-setup')
    }

    // Allow access to routes that should work for both authenticated and unauthenticated users
    if (to.meta.skipAuthRedirect) {
      return next()
    }

    // User authenticated without 2FA pending, redirect to main app
    return next('/trends')
  }

  // For non-guest routes, ensure user is loaded
  if (!user.value && !isLoading.value) {
    try {
      await fetchUser()
    } catch (e) {
      // Session invalid - redirect to login
      return next('/login')
    }
  }

  const isAuthenticated = !!user.value

  // Protected routes require authentication
  if (to.meta.requiresAuth && !isAuthenticated) {
    return next('/login')
  }

  // Check if 2FA setup is required (enforced but not configured)
  // Block navigation to any route except /2fa-setup
  if (isAuthenticated && needs2FASetup()) {
    if (to.path !== '/2fa-setup') {
      return next('/2fa-setup')
    }
  }

  // Check if onboarding is required (after 2FA check)
  const needsOnboarding = () => {
    return user.value?.organization?.onboarding_completed === false
  }

  if (isAuthenticated && !needs2FASetup() && needsOnboarding()) {
    if (to.path !== '/onboarding') {
      return next('/onboarding')
    }
  }

  // Role checks
  const userRole = user.value?.role || null
  const roles = Array.isArray(user.value?.roles) ? user.value.roles : []
  const isAdmin = userRole === 'admin' || roles.includes('admin')
  const isSuperAdmin = roles.includes('superadmin')
  const isEmployeeOnly = (userRole === 'employee' || roles.includes('employee')) && !isAdmin && !roles.includes('manager')

  // Employee-only role: restrict to own detail page and profile only
  if (isAuthenticated && isEmployeeOnly) {
    const empId = user.value?.employee_id
    const allowedPaths = ['/app/settings/profile', '/2fa-setup']
    const isOwnDetail = empId && to.path.startsWith(`/app/employee/${empId}/detail`)
    const isAllowed = allowedPaths.includes(to.path) || isOwnDetail

    if (!isAllowed) {
      if (empId) {
        return next(`/app/employee/${empId}/detail/utilization`)
      }
      return next('/app/settings/profile')
    }
  }

  // Admin-only routes
  if (to.meta.requiresAdmin && !isAdmin && !isSuperAdmin) {
    return next('/trends')
  }

  // Admin or Manager routes (settings pages)
  if (to.meta.requiresAdminOrManager) {
    const isManager = userRole === 'manager' || roles.includes('manager')
    if (!isAdmin && !isSuperAdmin && !isManager) {
      return next('/trends')
    }
  }

  // Superadmin-only routes
  if (to.meta.requiresSuperAdmin && !isSuperAdmin) {
    return next('/login')
  }

  // Permission-based route guard for managers
  // Admins bypass permission checks; employees are already handled above
  if (to.meta.requiredPermission && isAuthenticated && !isAdmin && !isSuperAdmin) {
    const permissions = Array.isArray(user.value?.permissions) ? user.value.permissions : []
    if (!permissions.includes(to.meta.requiredPermission)) {
      // Redirect to first permitted route, or profile as fallback
      const firstPermitted = permissions[0]
      const permissionRouteMap = {
        'trends.view': '/trends',
        'realtime.view': '/realtime',
        'employees.view': '/app/employee/active',
        'teams.view': '/app/teams',
        'screens.view': '/screens',
        'activities.view': '/activities/timeline',
        'alerts.view': '/alerts',
        'reports.view': '/reports',
        'schedules.view': '/app/settings/schedules',
      }
      const fallback = permissionRouteMap[firstPermitted] || '/app/settings/profile'
      return next(fallback)
    }
  }

  next()
})

export default router
